/**
 * Service Packages — admin CRUD UI for the new car-servicing vertical.
 *
 * Phase 1 (Feb 2026):
 *   • List packages with category / status / visibility chips
 *   • Inline visibility toggles (website / crm / mobile_app) — instant save
 *   • Open editor (dialog) to create / edit name, description, items,
 *     per-brand pricing, sort_order, hero image URL
 *   • Clone & archive actions
 *
 * Routes used:
 *   GET    /api/service-packages
 *   POST   /api/service-packages
 *   GET    /api/service-packages/{id}
 *   PUT    /api/service-packages/{id}
 *   DELETE /api/service-packages/{id}             (soft archive)
 *   POST   /api/service-packages/{id}/clone
 *   POST   /api/service-packages/{id}/visibility
 *   GET    /api/service-packages/brands
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Wrench, Pencil, Copy, Archive, Globe, Monitor, Smartphone, Layout,
  Trash2, Search, Loader2, Package,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import SparePartsMasterTab from '../components/servicePackages/SparePartsMasterTab';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABEL = {
  GENERAL: 'General Service',
  AC: 'AC Service',
  HEALTH_CHECK: 'Health Check-up',
  CUSTOM: 'Custom',
};

const CATEGORY_COLOR = {
  GENERAL: 'bg-blue-100 text-blue-800',
  AC: 'bg-cyan-100 text-cyan-800',
  HEALTH_CHECK: 'bg-emerald-100 text-emerald-800',
  CUSTOM: 'bg-slate-100 text-slate-700',
};

const blankPackage = () => ({
  id: null,
  name: '',
  category: 'GENERAL',
  description: '',
  items: [{ name: '', description: '', media_requirement: { type: 'none', positions: [] } }],
  pricing: [],
  visibility: { website: true, landing_page: true, crm: true, mobile_app: true },
  is_active: true,
  hero_image_url: '',
  sort_order: 0,
  // Flat-price (2026-05-23): one price wins for every make/model/fuel
  is_flat_price: false,
  flat_price: '',
  // OBD-2 (2026-05-27): every new service-lead bound to this package inherits
  // this flag as its initial `obd_scanning_enabled`. Defaults ON; admins can
  // turn it off for packages where OBD is irrelevant (pure exterior wash etc).
  requires_obd: true,
});

const blankPriceRule = () => ({ brand: '', model: '', fuel_type: '', price: '', currency: 'INR' });

const FUEL_TYPES = ['PETROL', 'DIESEL', 'CNG'];

export default function ServicePackagesPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('packages'); // 'packages' | 'spare-parts'
  const [packages, setPackages] = useState([]);
  const [brands, setBrands] = useState([]);
  const [makesCatalogue, setMakesCatalogue] = useState([]);   // [{name, models:[{name,fuels[]}]}]
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // package being edited
  const [saving, setSaving] = useState(false);
  const [addModelDialog, setAddModelDialog] = useState(null); // {rowIdx, brand} | null
  const [newModelDraft, setNewModelDraft] = useState({ name: '', fuels: [] });
  const [savingModel, setSavingModel] = useState(false);

  const token = () => localStorage.getItem('token');
  const authHeaders = () => ({ Authorization: `Bearer ${token()}` });

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/service-packages`, { headers: authHeaders() });
      setPackages(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const res = await axios.get(`${API_URL}/service-packages/brands`, { headers: authHeaders() });
      setBrands(res.data?.brands || []);
    } catch {
      // best-effort — fall back to manual brand entry in editor
    }
  };

  const fetchMakesModels = async () => {
    try {
      const res = await axios.get(`${API_URL}/service-packages/models`, { headers: authHeaders() });
      setMakesCatalogue(res.data?.makes || []);
    } catch {
      setMakesCatalogue([]);
    }
  };

  useEffect(() => {
    fetchPackages();
    fetchBrands();
    fetchMakesModels();
  }, []);

  const filtered = useMemo(() => {
    return packages.filter(p => {
      if (!showArchived && p.is_active === false) return false;
      if (filterCategory !== 'all' && p.category !== filterCategory) return false;
      if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [packages, search, filterCategory, showArchived]);

  const openCreate = () => {
    setEditing(blankPackage());
    setEditorOpen(true);
  };

  const openEdit = (pkg) => {
    const allRules = pkg.pricing || [];
    // Editor only manages new (model-based) rules. Legacy car_type-based
    // rules stay untouched in DB — the backend merges them back on save.
    const editableRules = allRules.filter(r => r.model);
    const legacyCount = allRules.length - editableRules.length;
    setEditing({
      ...pkg,
      items: pkg.items?.length ? pkg.items : [{ name: '', description: '' }],
      pricing: editableRules,
      _legacy_count: legacyCount,
      // Default flat-price fields if missing on legacy records.
      is_flat_price: !!pkg.is_flat_price,
      flat_price: pkg.flat_price ?? '',
      visibility: {
        website: pkg.visibility?.website ?? true,
        landing_page: pkg.visibility?.landing_page ?? true,
        crm: pkg.visibility?.crm ?? true,
        mobile_app: pkg.visibility?.mobile_app ?? true,
      },
      // OBD-2 (2026-05-27) — default ON for legacy packages that don't carry the field.
      requires_obd: pkg.requires_obd !== false,
    });
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (!editing.name.trim()) { toast.error('Name is required'); return; }

    // Flat-price mode: one price applies to every make/model/fuel.
    // Skip pricing-matrix validation entirely and demand a positive flat_price.
    if (editing.is_flat_price) {
      const fp = Number(editing.flat_price);
      if (!fp || fp <= 0) {
        toast.error('Enter a positive flat price (₹) before saving.');
        return;
      }
    } else {
      // Pricing-matrix validation (CEO-reported 2026-05-23 follow-up bug):
      // PREVIOUSLY this was a HARD early-exit if ANY existing pricing
      // rule was incomplete — but legacy packages on prod have ~687 rules
      // some of which drifted to null/empty fields over time, so editing
      // ITEMS on those packages silently failed (toast flashed once,
      // disappeared, items "came back" on reopen because PUT never fired).
      //
      // Now we ONLY block save if the user has typed a partial row in this
      // session (a row with SOME fields filled but not ALL). Pre-existing
      // rows that are fully blank or auto-imported with stale data are
      // silently dropped by the `.filter(...)` in the payload below — they
      // don't block the save anymore.
      const incompleteNewRow = (editing.pricing || []).find(p => {
        const hasAny =
          (p.brand && p.brand.trim()) ||
          (p.model && p.model.trim()) ||
          p.fuel_type ||
          (p.price !== '' && p.price !== null && p.price !== undefined);
        const hasAll =
          p.brand?.trim() && p.model?.trim() && p.fuel_type &&
          (p.price !== '' && p.price !== null && p.price !== undefined);
        // partial = some-but-not-all dimensions present — that's the only
        // case we block, because saving it as-is would create a useless row.
        return hasAny && !hasAll;
      });
      if (incompleteNewRow) {
        toast.error('A pricing row is missing Make / Model / Fuel / Price. Complete or remove it.');
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        category: editing.category,
        description: editing.description,
        items: editing.items.filter(i => i.name?.trim()),
        // When flat-price ON, send an empty pricing array — backend
        // ignores it anyway because resolve_package_price short-circuits.
        // Otherwise send ONLY complete rows — partial / blank rows are
        // dropped here so they no longer block save.
        pricing: editing.is_flat_price ? [] : (editing.pricing || []).filter(p =>
          p.brand?.trim() && p.model?.trim() && p.fuel_type
          && p.price !== '' && p.price !== null && p.price !== undefined
        ),
        visibility: editing.visibility,
        is_active: editing.is_active,
        hero_image_url: editing.hero_image_url,
        sort_order: Number(editing.sort_order) || 0,
        is_flat_price: !!editing.is_flat_price,
        flat_price: editing.is_flat_price ? Number(editing.flat_price) : null,
        // OBD-2 (2026-05-27): inherited by every new service-lead bound to
        // this package as its initial obd_scanning_enabled value.
        requires_obd: editing.requires_obd !== false,
      };
      if (editing.id) {
        await axios.put(`${API_URL}/service-packages/${editing.id}`, payload, { headers: authHeaders() });
        toast.success('Package updated');
      } else {
        await axios.post(`${API_URL}/service-packages`, payload, { headers: authHeaders() });
        toast.success('Package created');
      }
      setEditorOpen(false);
      fetchPackages();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleClone = async (pkg) => {
    try {
      await axios.post(`${API_URL}/service-packages/${pkg.id}/clone`, {}, { headers: authHeaders() });
      toast.success(`Cloned "${pkg.name}"`);
      fetchPackages();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Clone failed');
    }
  };

  const handleArchive = async (pkg) => {
    if (!window.confirm(`Archive "${pkg.name}"? It will be hidden from all surfaces.`)) return;
    try {
      await axios.delete(`${API_URL}/service-packages/${pkg.id}`, { headers: authHeaders() });
      toast.success(`Archived "${pkg.name}"`);
      fetchPackages();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Archive failed');
    }
  };

  const handlePurge = async (pkg) => {
    const msg = (
      `PERMANENTLY DELETE "${pkg.name}"?\n\n` +
      `This cannot be undone. The package will be removed from the database ` +
      `entirely.\n\n` +
      `Note: deletion is blocked if any service lead or job still ` +
      `references this package.`
    );
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(
        `${API_URL}/service-packages/${pkg.id}/purge`,
        { headers: authHeaders() },
      );
      toast.success(`Permanently deleted "${pkg.name}"`);
      fetchPackages();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Permanent delete failed';
      // 409s come back with a human-readable reason (FK refs, still active).
      toast.error(detail, { duration: 8000 });
    }
  };

  const handleVisibilityToggle = async (pkg, channel, visible) => {
    try {
      await axios.post(
        `${API_URL}/service-packages/${pkg.id}/visibility`,
        { channel, visible },
        { headers: authHeaders() }
      );
      setPackages(prev => prev.map(p => p.id === pkg.id
        ? { ...p, visibility: { ...p.visibility, [channel]: visible } }
        : p
      ));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Visibility toggle failed');
    }
  };

  // ── Editor handlers ──
  const updateField = (key, value) => setEditing(prev => ({ ...prev, [key]: value }));
  const updateItem = (idx, key, value) => setEditing(prev => ({
    ...prev,
    items: prev.items.map((it, i) => i === idx ? { ...it, [key]: value } : it),
  }));
  const updateItemMediaType = (idx, type) => setEditing(prev => ({
    ...prev,
    items: prev.items.map((it, i) => {
      if (i !== idx) return it;
      // When switching type, reset positions to a sensible default:
      //   none → []  ·  photo → ['after']  ·  video → ['single']
      const positions =
        type === 'none' ? [] :
        type === 'photo' ? ['after'] :
        ['single'];
      return { ...it, media_requirement: { type, positions } };
    }),
  }));
  const updateItemPositions = (idx, positions) => setEditing(prev => ({
    ...prev,
    items: prev.items.map((it, i) => {
      if (i !== idx) return it;
      const mr = it.media_requirement || { type: 'none', positions: [] };
      return { ...it, media_requirement: { ...mr, positions } };
    }),
  }));
  const addItem = () => setEditing(prev => ({
    ...prev,
    items: [...prev.items, { name: '', description: '', media_requirement: { type: 'none', positions: [] } }],
  }));
  const removeItem = (idx) => setEditing(prev => ({
    ...prev,
    items: prev.items.filter((_, i) => i !== idx),
  }));
  const updatePrice = (idx, key, value) => setEditing(prev => ({
    ...prev,
    pricing: prev.pricing.map((p, i) => {
      if (i !== idx) return p;
      const next = { ...p, [key]: value };
      // Cascade: clearing/changing brand should reset model + fuel since
      // they're brand-specific.
      if (key === 'brand') {
        next.model = '';
        next.fuel_type = '';
      } else if (key === 'model') {
        // Changing model may invalidate the chosen fuel — clear it so the
        // user re-picks from the model's available fuels.
        next.fuel_type = '';
      }
      return next;
    }),
  }));
  const addPriceRow = () => setEditing(prev => ({
    ...prev,
    pricing: [...prev.pricing, blankPriceRule()],
  }));
  const removePriceRow = (idx) => setEditing(prev => ({
    ...prev,
    pricing: prev.pricing.filter((_, i) => i !== idx),
  }));

  // Lookup helpers driven off the catalogue
  const modelsForBrand = (brand) => {
    if (!brand) return [];
    const m = makesCatalogue.find(x => x.name === brand);
    return m?.models || [];
  };
  const fuelsForBrandModel = (brand, modelName) => {
    if (!brand || !modelName) return FUEL_TYPES;
    const m = modelsForBrand(brand).find(x => x.name === modelName);
    return (m?.fuels && m.fuels.length) ? m.fuels : FUEL_TYPES;
  };

  const openAddModel = (rowIdx) => {
    const brand = editing?.pricing?.[rowIdx]?.brand;
    if (!brand) {
      toast.error('Pick a Make first, then add a new Model under it.');
      return;
    }
    setNewModelDraft({ name: '', fuels: [] });
    setAddModelDialog({ rowIdx, brand });
  };

  const submitAddModel = async () => {
    const name = (newModelDraft.name || '').trim();
    if (!name) { toast.error('Model name is required'); return; }
    if (!newModelDraft.fuels.length) { toast.error('Pick at least one fuel type'); return; }
    setSavingModel(true);
    try {
      await axios.post(
        `${API_URL}/service-packages/models`,
        { brand: addModelDialog.brand, model: name, fuels: newModelDraft.fuels },
        { headers: authHeaders() }
      );
      toast.success(`Added ${addModelDialog.brand} ${name}`);
      // Refresh catalogue, then auto-select the new model on the row
      const res = await axios.get(`${API_URL}/service-packages/models`, { headers: authHeaders() });
      const fresh = res.data?.makes || [];
      setMakesCatalogue(fresh);
      const rowIdx = addModelDialog.rowIdx;
      setEditing(prev => ({
        ...prev,
        pricing: prev.pricing.map((p, i) => i === rowIdx ? { ...p, model: name, fuel_type: '' } : p),
      }));
      setAddModelDialog(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add model');
    } finally {
      setSavingModel(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="service-packages-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="bg-orange-100 p-3 rounded-xl">
            <Wrench className="h-6 w-6 text-orange-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Service Packages</h1>
            <p className="text-sm text-slate-500">
              Define General / AC / Health Check-up packages with per-brand pricing and per-channel visibility.
            </p>
          </div>
        </div>
        <Button onClick={openCreate} className="gap-2 bg-orange-600 hover:bg-orange-700" data-testid="create-package-btn">
          <Plus className="h-4 w-4" /> New Package
        </Button>
      </div>

      {/* Tab switcher: Packages vs Spare Parts Master */}
      <div className="flex items-center gap-1 mb-4 border-b" data-testid="sp-tabs">
        <button
          onClick={() => setActiveTab('packages')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'packages'
              ? 'border-orange-600 text-orange-700'
              : 'border-transparent text-slate-500 hover:text-slate-900'
          }`}
          data-testid="sp-tab-packages"
        >
          <Wrench className="h-4 w-4 inline mr-1.5" />
          Packages
        </button>
        <button
          onClick={() => setActiveTab('spare-parts')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'spare-parts'
              ? 'border-orange-600 text-orange-700'
              : 'border-transparent text-slate-500 hover:text-slate-900'
          }`}
          data-testid="sp-tab-spare-parts"
        >
          <Package className="h-4 w-4 inline mr-1.5" />
          Spare Parts Master
        </button>
      </div>

      {activeTab === 'spare-parts' ? (
        <SparePartsMasterTab user={user} />
      ) : (<>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name..."
            className="pl-9"
            data-testid="package-search"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[180px]" data-testid="filter-category">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {Object.entries(CATEGORY_LABEL).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid="show-archived" />
          Show archived
        </label>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left text-xs font-semibold text-slate-600 uppercase px-4 py-3">Package</th>
              <th className="text-left text-xs font-semibold text-slate-600 uppercase px-4 py-3">Category</th>
              <th className="text-left text-xs font-semibold text-slate-600 uppercase px-4 py-3">Items</th>
              <th className="text-left text-xs font-semibold text-slate-600 uppercase px-4 py-3">Pricing rules</th>
              <th className="text-center text-xs font-semibold text-slate-600 uppercase px-4 py-3">Visibility</th>
              <th className="text-center text-xs font-semibold text-slate-600 uppercase px-4 py-3">Status</th>
              <th className="text-right text-xs font-semibold text-slate-600 uppercase px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Loading…
              </td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                No packages yet. Click <strong>New Package</strong> to create one.
              </td></tr>
            )}
            {filtered.map(pkg => (
              <tr key={pkg.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`pkg-row-${pkg.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-900">{pkg.name}</div>
                  {pkg.description && (
                    <div className="text-xs text-slate-500 line-clamp-1 max-w-xs">{pkg.description}</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Badge className={CATEGORY_COLOR[pkg.category] || CATEGORY_COLOR.CUSTOM}>
                    {CATEGORY_LABEL[pkg.category] || pkg.category}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-sm text-slate-700">{pkg.items?.length || 0}</td>
                <td className="px-4 py-3 text-sm text-slate-700">{pkg.pricing?.length || 0}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 justify-center">
                    <VisibilityIcon
                      icon={Globe} on={!!pkg.visibility?.website} label="Website"
                      onClick={() => handleVisibilityToggle(pkg, 'website', !pkg.visibility?.website)}
                    />
                    <VisibilityIcon
                      icon={Layout} on={!!pkg.visibility?.landing_page} label="Landing"
                      onClick={() => handleVisibilityToggle(pkg, 'landing_page', !pkg.visibility?.landing_page)}
                    />
                    <VisibilityIcon
                      icon={Monitor} on={!!pkg.visibility?.crm} label="CRM"
                      onClick={() => handleVisibilityToggle(pkg, 'crm', !pkg.visibility?.crm)}
                    />
                    <VisibilityIcon
                      icon={Smartphone} on={!!pkg.visibility?.mobile_app} label="Mobile"
                      onClick={() => handleVisibilityToggle(pkg, 'mobile_app', !pkg.visibility?.mobile_app)}
                    />
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  {pkg.is_active ? (
                    <Badge className="bg-green-100 text-green-800">Active</Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-600">Archived</Badge>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(pkg)} data-testid={`edit-${pkg.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleClone(pkg)} title="Clone" data-testid={`clone-${pkg.id}`}>
                      <Copy className="h-4 w-4" />
                    </Button>
                    {pkg.is_active && (
                      <Button size="sm" variant="ghost" onClick={() => handleArchive(pkg)} title="Archive" data-testid={`archive-${pkg.id}`}>
                        <Archive className="h-4 w-4 text-slate-500" />
                      </Button>
                    )}
                    {!pkg.is_active && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handlePurge(pkg)}
                        title="Permanently delete (cannot be undone)"
                        data-testid={`purge-${pkg.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit' : 'Create'} Service Package</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              {/* Basic */}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-slate-700 mb-1 block">Name *</label>
                  <Input value={editing.name} onChange={e => updateField('name', e.target.value)} placeholder="e.g. General Service" data-testid="editor-name" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1 block">Category</label>
                  <Select value={editing.category} onValueChange={v => updateField('category', v)}>
                    <SelectTrigger data-testid="editor-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORY_LABEL).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1 block">Sort order</label>
                  <Input type="number" value={editing.sort_order} onChange={e => updateField('sort_order', e.target.value)} />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-slate-700 mb-1 block">Description (shown to customer)</label>
                  <Textarea value={editing.description} onChange={e => updateField('description', e.target.value)} rows={2} placeholder="Routine maintenance — engine oil + filters + brakes" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-slate-700 mb-1 block">Hero image URL (optional)</label>
                  <Input value={editing.hero_image_url} onChange={e => updateField('hero_image_url', e.target.value)} placeholder="https://..." />
                </div>
              </div>

              {/* Items */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-slate-800">Service items</h3>
                  <Button size="sm" variant="outline" onClick={addItem} className="gap-1" data-testid="editor-add-item">
                    <Plus className="h-3.5 w-3.5" /> Add item
                  </Button>
                </div>
                <div className="space-y-3">
                  {editing.items.map((item, idx) => {
                    const mr = item.media_requirement || { type: 'none', positions: [] };
                    const positions = mr.positions || [];
                    const hasPos = (p) => positions.includes(p);
                    const togglePos = (p) => {
                      const next = hasPos(p) ? positions.filter(x => x !== p) : [...positions, p];
                      updateItemPositions(idx, next);
                    };
                    return (
                      <div key={item.id || `new-${idx}`} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2" data-testid={`item-row-${idx}`}>
                        <div className="grid grid-cols-12 gap-2 items-center">
                          <Input
                            className="col-span-5"
                            placeholder="Item name (e.g. Engine oil change)"
                            value={item.name || ''}
                            onChange={e => updateItem(idx, 'name', e.target.value)}
                            data-testid={`item-name-${idx}`}
                          />
                          <Input
                            className="col-span-6"
                            placeholder="Optional description"
                            value={item.description || ''}
                            onChange={e => updateItem(idx, 'description', e.target.value)}
                          />
                          <Button size="sm" variant="ghost" onClick={() => removeItem(idx)} className="col-span-1" title="Remove item" data-testid={`item-delete-${idx}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                        {/* Media capture requirement controls */}
                        <div className="flex flex-wrap items-center gap-3 pl-1 pt-1 text-xs text-slate-600">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-700">Proof:</span>
                            <select
                              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs"
                              value={mr.type}
                              onChange={e => updateItemMediaType(idx, e.target.value)}
                              data-testid={`item-media-type-${idx}`}
                            >
                              <option value="none">No media</option>
                              <option value="photo">Photo</option>
                              <option value="video">Video</option>
                            </select>
                          </div>
                          {mr.type === 'photo' && (
                            <div className="flex items-center gap-2" data-testid={`item-photo-positions-${idx}`}>
                              <span className="text-slate-500">Positions:</span>
                              <label className="inline-flex items-center gap-1 cursor-pointer">
                                <input type="checkbox" checked={hasPos('before')} onChange={() => togglePos('before')} className="h-3.5 w-3.5" />
                                <span>Before</span>
                              </label>
                              <label className="inline-flex items-center gap-1 cursor-pointer">
                                <input type="checkbox" checked={hasPos('after')} onChange={() => togglePos('after')} className="h-3.5 w-3.5" />
                                <span>After</span>
                              </label>
                            </div>
                          )}
                          {mr.type === 'video' && (
                            <div className="flex items-center gap-3" data-testid={`item-video-positions-${idx}`}>
                              <span className="text-slate-500">Positions:</span>
                              <label className="inline-flex items-center gap-1 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`video-pos-${idx}`}
                                  checked={positions.length === 1 && positions[0] === 'single'}
                                  onChange={() => updateItemPositions(idx, ['single'])}
                                  className="h-3.5 w-3.5"
                                />
                                <span>Single clip</span>
                              </label>
                              <label className="inline-flex items-center gap-1 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`video-pos-${idx}`}
                                  checked={positions.length === 1 && positions[0] === 'before'}
                                  onChange={() => updateItemPositions(idx, ['before'])}
                                  className="h-3.5 w-3.5"
                                />
                                <span>Before only</span>
                              </label>
                              <label className="inline-flex items-center gap-1 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`video-pos-${idx}`}
                                  checked={positions.length === 1 && positions[0] === 'after'}
                                  onChange={() => updateItemPositions(idx, ['after'])}
                                  className="h-3.5 w-3.5"
                                />
                                <span>After only</span>
                              </label>
                              <label className="inline-flex items-center gap-1 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`video-pos-${idx}`}
                                  checked={positions.length === 2 && positions.includes('before') && positions.includes('after')}
                                  onChange={() => updateItemPositions(idx, ['before', 'after'])}
                                  className="h-3.5 w-3.5"
                                />
                                <span>Before + After</span>
                              </label>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Pricing rules */}
              <div className="border-t pt-4">
                {/* Flat-price toggle (CEO-requested 2026-05-23). When ON,
                    one price applies to EVERY make / model / fuel — used
                    for packages like "Car Wash" where vehicle-specific
                    pricing is overkill. Matrix is hidden while ON. */}
                <div className="mb-3 p-3 rounded-md border border-emerald-200 bg-emerald-50/40">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={!!editing.is_flat_price}
                      onChange={(e) => updateField('is_flat_price', e.target.checked)}
                      data-testid="flat-price-toggle"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-emerald-900">
                        Flat price — one price for every make, model & fuel
                      </div>
                      <div className="text-[11px] text-emerald-700">
                        When ON, the make/model/fuel matrix below is ignored.
                        Any vehicle that books this package gets the flat price.
                      </div>
                    </div>
                  </label>
                  {editing.is_flat_price && (
                    <div className="mt-3 grid grid-cols-[auto_180px] items-center gap-2">
                      <label className="text-xs font-semibold text-slate-700">
                        Flat price ({activeCurrencySymbol()})
                      </label>
                      <Input
                        type="number"
                        min={0}
                        value={editing.flat_price}
                        onChange={(e) => updateField('flat_price', e.target.value)}
                        placeholder="e.g. 599"
                        className="bg-white"
                        data-testid="flat-price-input"
                      />
                    </div>
                  )}
                </div>
                {/* Hide the full make/model/fuel matrix when flat-price is ON. */}
                {!editing.is_flat_price && (
                <>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800">Pricing rules</h3>
                    <p className="text-xs text-slate-500">
                      Each rule prices one <strong>Make + Model + Fuel</strong> combination. Pick a Make,
                      then the Model dropdown filters automatically. Use <em>+ Add new model</em> if a
                      variant is missing.
                    </p>
                  </div>
                  <Button size="sm" variant="outline" onClick={addPriceRow} className="gap-1" data-testid="editor-add-price">
                    <Plus className="h-3.5 w-3.5" /> Add rule
                  </Button>
                </div>
                {editing._legacy_count > 0 && (
                  <div className="mb-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-900" data-testid="legacy-rules-banner">
                    <strong>{editing._legacy_count}</strong> legacy car-type pricing rule
                    {editing._legacy_count === 1 ? '' : 's'} kept untouched on this package
                    (Hatchback / Sedan / Compact SUV / etc.). They continue to power the
                    public landing page. New rules added below use the strict
                    Make + Model + Fuel schema.
                  </div>
                )}
                {editing.pricing.length > 0 && (
                  <div className="grid grid-cols-12 gap-2 px-1 mb-1">
                    <div className="col-span-3 text-[11px] uppercase font-semibold text-slate-500 tracking-wide">Make</div>
                    <div className="col-span-4 text-[11px] uppercase font-semibold text-slate-500 tracking-wide">Model</div>
                    <div className="col-span-2 text-[11px] uppercase font-semibold text-slate-500 tracking-wide">Fuel</div>
                    <div className="col-span-2 text-[11px] uppercase font-semibold text-slate-500 tracking-wide">Price ({activeCurrencySymbol()})</div>
                    <div className="col-span-1" />
                  </div>
                )}
                <div className="space-y-2">
                  {editing.pricing.length === 0 && (
                    <p className="text-xs text-slate-500 italic">No pricing rules yet — add at least one to make this package quotable.</p>
                  )}
                  {editing.pricing.map((p, idx) => {
                    const brandModels = modelsForBrand(p.brand);
                    const allowedFuels = fuelsForBrandModel(p.brand, p.model);
                    return (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`price-row-${idx}`}>
                        {/* Make */}
                        <div className="col-span-3">
                          <Select
                            value={p.brand?.trim() ? p.brand : ''}
                            onValueChange={v => updatePrice(idx, 'brand', v)}
                          >
                            <SelectTrigger data-testid={`price-brand-${idx}`}>
                              <SelectValue placeholder="Pick a Make" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                              {(makesCatalogue.length ? makesCatalogue.map(m => m.name) : brands).map(b => (
                                <SelectItem key={b} value={b}>{b}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Model — cascading */}
                        <div className="col-span-4">
                          <Select
                            value={p.model || ''}
                            onValueChange={v => {
                              if (v === '__add__') {
                                openAddModel(idx);
                              } else {
                                updatePrice(idx, 'model', v);
                              }
                            }}
                            disabled={!p.brand}
                          >
                            <SelectTrigger data-testid={`price-model-${idx}`}>
                              <SelectValue placeholder={p.brand ? 'Pick a Model' : 'Pick Make first'} />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                              {brandModels.map(m => (
                                <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>
                              ))}
                              {p.brand && (
                                <SelectItem value="__add__" data-testid={`price-add-model-${idx}`}>
                                  <span className="text-orange-700 font-medium">+ Add new model…</span>
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Fuel — narrowed by model when known */}
                        <div className="col-span-2">
                          <Select
                            value={p.fuel_type || ''}
                            onValueChange={v => updatePrice(idx, 'fuel_type', v)}
                            disabled={!p.model}
                          >
                            <SelectTrigger data-testid={`price-fuel-${idx}`}>
                              <SelectValue placeholder={p.model ? 'Fuel' : '—'} />
                            </SelectTrigger>
                            <SelectContent>
                              {allowedFuels.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Price — wider to comfortably fit 5-digit values like 99999 */}
                        <div className="col-span-2">
                          <Input
                            type="number" min={0} max={99999}
                            placeholder={activeCurrencySymbol()}
                            value={p.price}
                            onChange={e => updatePrice(idx, 'price', e.target.value)}
                            data-testid={`price-amount-${idx}`}
                          />
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => removePriceRow(idx)} className="col-span-1" data-testid={`price-remove-${idx}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
                </>
                )}
              </div>

              {/* Visibility */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-slate-800 mb-2">Visibility</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { key: 'website', icon: Globe, label: 'Website' },
                    { key: 'landing_page', icon: Layout, label: 'Landing Page' },
                    { key: 'crm', icon: Monitor, label: 'CRM' },
                    { key: 'mobile_app', icon: Smartphone, label: 'Mobile App' },
                  ].map(({ key, icon: Icon, label }) => (
                    <label key={key} className="flex items-center justify-between gap-2 px-3 py-2 border rounded-lg cursor-pointer">
                      <span className="flex items-center gap-2 text-sm font-medium">
                        <Icon className="h-4 w-4 text-slate-600" /> {label}
                      </span>
                      <Switch
                        checked={!!editing.visibility[key]}
                        onCheckedChange={v => updateField('visibility', { ...editing.visibility, [key]: v })}
                        data-testid={`vis-${key}`}
                      />
                    </label>
                  ))}
                </div>
              </div>

              <div className="border-t pt-3 space-y-3">
                <label className="flex items-center gap-2 text-sm">
                  <Switch
                    checked={!!editing.is_active}
                    onCheckedChange={v => updateField('is_active', v)}
                    data-testid="vis-active"
                  />
                  Active (uncheck to archive)
                </label>
                {/*
                 * OBD-2 opt-in (2026-05-27). Default ON. Each new service-lead
                 * bound to this package inherits this flag as its initial
                 * obd_scanning_enabled value, which then drives:
                 *   • the CRM Service Ops Modal OBD2 tab's "scan required" pill
                 *   • the mechanic-app gating on Mark Complete (PR2)
                 * Turn OFF for packages where OBD makes no sense (e.g. pure
                 * exterior detailing).
                 */}
                <label className="flex items-start gap-2 text-sm" data-testid="pkg-requires-obd-row">
                  <Switch
                    checked={editing.requires_obd !== false}
                    onCheckedChange={v => updateField('requires_obd', v)}
                    data-testid="pkg-requires-obd"
                  />
                  <span className="flex flex-col">
                    <span className="font-medium">Require OBD-2 scan for this package</span>
                    <span className="text-xs text-slate-500">
                      Every new service-lead bound to this package will need a mechanic-completed OBD scan before it can be marked done.
                    </span>
                  </span>
                </label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="editor-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editing?.id ? 'Save changes' : 'Create package'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add-new-model sub-dialog */}
      <Dialog open={!!addModelDialog} onOpenChange={(o) => { if (!o) setAddModelDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="add-model-dialog">
          <DialogHeader>
            <DialogTitle>
              Add a new model under {addModelDialog?.brand}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Model name *</label>
              <Input
                value={newModelDraft.name}
                onChange={e => setNewModelDraft(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Swift Hybrid"
                data-testid="add-model-name"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">
                Available fuels * (pick all that apply)
              </label>
              <div className="flex flex-wrap gap-2">
                {FUEL_TYPES.map(f => {
                  const checked = newModelDraft.fuels.includes(f);
                  return (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setNewModelDraft(prev => ({
                        ...prev,
                        fuels: checked
                          ? prev.fuels.filter(x => x !== f)
                          : [...prev.fuels, f],
                      }))}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                        checked
                          ? 'bg-orange-600 text-white border-orange-600'
                          : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                      }`}
                      data-testid={`add-model-fuel-${f}`}
                    >
                      {f}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddModelDialog(null)} disabled={savingModel}>Cancel</Button>
            <Button
              onClick={submitAddModel}
              disabled={savingModel}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="add-model-save"
            >
              {savingModel ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Add model
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>)}
    </div>
  );
}

function VisibilityIcon({ icon: Icon, on, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${label}: ${on ? 'Visible' : 'Hidden'} — click to toggle`}
      className={`p-1.5 rounded-md transition-colors ${
        on ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
           : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
      }`}
      data-testid={`vis-toggle-${label.toLowerCase()}`}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}
import { activeCurrencySymbol } from '@/utils/currency';
